'use client';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import React, { useEffect, useState, useRef, useCallback } from 'react';

type TableOfContent = {
  id: string;
  title: string;
};

type TableOfContentsProps = {
  navbarOffset: number;
  toc: TableOfContent[];
};

export default function TableOfContents({
  navbarOffset,
  toc,
}: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      if (visibleEntries.length > 0) {
        setActiveId(visibleEntries[0].target.id);
      }
    },
    [],
  );

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px 0px -40% 0px',
      threshold: 0.5,
    });

    toc.forEach((item) => {
      const section = document.getElementById(item.id);
      if (section) observerRef?.current?.observe(section);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [toc, handleIntersection]);

  const scrollToSection = useCallback(
    (id: string) => {
      const element = document.getElementById(id);
      if (element) {
        const offsetPosition =
          element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: offsetPosition - navbarOffset,
          behavior: 'smooth',
        });
        setActiveId(id);
      }
    },
    [navbarOffset],
  );

  return (
    <nav className="table-of-contents tablet:p-4 bg-background tablet:rounded-2xl desktop:max-w-[328px] desktop:ml-auto laptop:mb-8 mb-0 w-full rounded-lg border border-[#CDDFFD] p-2">
      <p className="tablet:text-[17px] p-2 text-[15px] leading-normal font-[700]">
        Table of contents
      </p>
      <ul data-test="blog-table-of-contents">
        {toc.map(({ id, title }) => (
          <li key={id} onClick={() => scrollToSection(id)}>
            <p
              className={cn(
                'tablet:text-[17px] rounded-lg p-2 text-[15px] leading-[1.5] font-medium hover:cursor-pointer hover:bg-[#F4F6FF] active:bg-transparent',
                activeId === id ? 'desktop:bg-[#F4F6FF]' : '',
              )}
            >
              {title}
            </p>
          </li>
        ))}
      </ul>
    </nav>
  );
}
